import { debounce } from "lodash";
import * as React from "react";
import { FC, useEffect, useMemo, useState } from "react";
import { connect } from "react-redux";
import { actions, Form } from "react-redux-form";
import { useLocation, useParams } from "react-router";
import Alert from "react-s-alert";
import { bindActionCreators } from "redux";
import {
    GroupTrialLaboratoryCreateBreadcrumbs,
    GroupTrialLaboratoryDetailsBreadcrumbs,
    TrialGroupLaboratoryCreateBreadcrumbs,
    TrialGroupLaboratoryDetailsBreadcrumbs,
} from "src/helpers/breadcrumbHelper";
import useNavigate from "src/hooks/useNavigate";
import { ITrialGroupLaboratoryReduxFormState } from "src/reducers/reactReduxForms/trialGroupLaboratory";
import * as NavigationActions from "../../../actions/navigation";
import * as TrialGroupActions from "../../../actions/trialGroup";
import * as TrialGroupLaboratoryActions from "../../../actions/trialGroupLaboratory";
import { NotificationAlert } from "../../../components/common/index";
import {
    CheckboxFormInput,
    DatePickerFormInputFabric,
    SelectFormInput,
} from "../../../components/form/index";
import { RestrictedLayout } from "../../../components/layouts/index";
import {
    FontAwesomeIcon,
    FontAwesomeIcons,
} from "../../../constants/fontAwesomeIcons";
import * as Dtos from "../../../dtos/Tmd.dtos";
import { RequestState } from "../../../enumerations/RequestState";
import { renderRegistrationFormSaveBlock } from "../../../helpers/alert";
import * as AuthenticationHelper from "../../../helpers/authentication";
import {
    RegistrationComponent,
    renderFormControls,
} from "../../../helpers/formHelper";
import {
    findLookup,
    generateOptionsFromLookup,
} from "../../../helpers/lookupHelper";
import {
    GroupTrialLabSubMenu,
    TrialGroupLabSubMenu,
} from "../../../helpers/subMenuHelper";
import { RegistrationFormComponent } from "../../../interfaces/forms/IRegistrationComponent";
import { ITmdState } from "../../../reducers/index";
import { IRegistrationFormState } from "../../../reducers/reactReduxForms/index";

import Link from "src/components/routing/Link";
import "./TrialGroupLaboratoryPage.scss";

interface TrialGroupLaboratoryPageProps {
    form: Dtos.TrialGroupLaboratory & IRegistrationFormState;
    formState: Dtos.FormState;
    formProperties: Dtos.FormProperty[];
    reduxFormState: ITrialGroupLaboratoryReduxFormState;
    trialGroupLaboratory: Dtos.TrialGroupLaboratory;
    lookups: Dtos.Lookup[];
    loadingTrialGroupLaboratory: boolean;
    loadTrialGroupLaboratorySuccess: boolean;
    loadTrialGroupLaboratoryFailure: boolean;
    loadTrialGroupLaboratoryErrorCode: string;
    savingTrialGroupLaboratory: boolean;
    saveTrialGroupLaboratorySuccess: boolean;
    saveTrialGroupLaboratoryFailure: boolean;
    creatingTrialGroupLaboratory: boolean;
    createTrialGroupLaboratorySuccess: boolean;
    createTrialGroupLaboratoryFailure: boolean;
    validatingTrialGroupLaboratory: boolean;
    validationFailures: Dtos.ResponseError[];
    user: Dtos.User;
    permissions?: Dtos.PersonnelTmdPermissions[];
    trialGroup: Dtos.TrialGroup;
    loadingTrialGroup: boolean;
    loadTrialGroupSuccess: boolean;
    loadTrialGroupFailure: boolean;
    loadTrialGroupErrorCode: string;
    clearTrialGroupLaboratory: TrialGroupLaboratoryActions.ITrialGroupLaboratoryClearActionCreator;
    loadTrialGroupLaboratory: TrialGroupLaboratoryActions.ITrialGroupLaboratoryLoadByIdActionCreator;
    saveTrialGroupLaboratory: TrialGroupLaboratoryActions.ITrialGroupLaboratorySaveActionCreator;
    createTrialGroupLaboratory: TrialGroupLaboratoryActions.ITrialGroupLaboratoryCreateActionCreator;
    validateTrialGroupLaboratory: TrialGroupLaboratoryActions.ITrialGroupLaboratoryValidateActionCreator;
    loadForm: typeof actions.load;
    changeForm: typeof actions.change;
    resetForm: typeof actions.reset;
    setBreadcrumbs: NavigationActions.ISetBreadcrumbsActionCreator;
    setDrawerItems: NavigationActions.ISetDrawerItemsActionCreator;
    loadTrialGroup: TrialGroupActions.ITrialGroupLoadByIdActionCreator;
    clearTrialGroup: TrialGroupActions.ITrialGroupClearActionCreator;
}

const reduxFormName: string = "reduxForms.trialGroupLaboratory";

const TrialGroupLaboratoryPage: FC<TrialGroupLaboratoryPageProps> = ({
    form,
    formState,
    formProperties,
    trialGroupLaboratory,
    lookups,
    loadingTrialGroupLaboratory,
    loadTrialGroupLaboratoryFailure,
    loadTrialGroupLaboratoryErrorCode,
    savingTrialGroupLaboratory,
    saveTrialGroupLaboratorySuccess,
    saveTrialGroupLaboratoryFailure,
    creatingTrialGroupLaboratory,
    validatingTrialGroupLaboratory,
    validationFailures,
    permissions,
    trialGroup,
    loadingTrialGroup,
    loadTrialGroupFailure,
    loadTrialGroupErrorCode,
    clearTrialGroupLaboratory,
    loadTrialGroupLaboratory,
    saveTrialGroupLaboratory,
    createTrialGroupLaboratory,
    validateTrialGroupLaboratory,
    loadForm,
    changeForm,
    resetForm,
    setBreadcrumbs,
    setDrawerItems,
    loadTrialGroup,
    clearTrialGroup
}) => {
    //
    // -- Params --
    const { trialId } = useParams<{ trialId: string }>();
    const { groupId } = useParams<{ groupId: string }>();
    const { trialGroupId } = useParams<{ trialGroupId: string }>();
    const { trialGroupLaboratoryId } = useParams<{
        trialGroupLaboratoryId: string;
    }>();
    const trialIdNumber = parseInt(trialId, 10);
    const groupIdNumber = parseInt(groupId, 10);
    const trialGroupIdNumber = parseInt(trialGroupId, 10);
    const trialGroupLaboratoryIdNumber = parseInt(trialGroupLaboratoryId, 10);

    //
    // -- States --
    const [saveAndReturn, setSaveAndReturn] = useState<boolean>(false);
    const [formInitalised, setFormInitalised] = useState<boolean>(false);
    const [hasBeenSubmitted, setHasBeenSubmitted] = useState<boolean>(false);

    //
    // -- Hooks --
    const navigate = useNavigate();
    const location = useLocation();


    // Load Trial Group Laboratory & Trial Group
    useEffect(() => {
        const baseRoute = location?.pathname?.split("/")[1];

        if (
            trialIdNumber &&
            groupIdNumber &&
            trialGroupIdNumber &&
            trialGroupLaboratoryIdNumber &&
            !trialGroupLaboratory
        ) {
            const breadcrumbSetter =
                baseRoute === "trials"
                    ? TrialGroupLaboratoryDetailsBreadcrumbs
                    : GroupTrialLaboratoryDetailsBreadcrumbs;

            setBreadcrumbs(
                breadcrumbSetter(
                    trialIdNumber,
                    groupIdNumber,
                    trialGroupIdNumber,
                    trialGroupLaboratoryIdNumber
                )
            );

            loadTrialGroupLaboratory(trialGroupLaboratoryIdNumber);
        } else if (
            trialIdNumber &&
            trialGroupIdNumber &&
            !trialGroupLaboratoryIdNumber &&
            !trialGroupLaboratory
        ) {
            const breadcrumbSetter =
                baseRoute === "trials"
                    ? TrialGroupLaboratoryCreateBreadcrumbs
                    : GroupTrialLaboratoryCreateBreadcrumbs;

            setBreadcrumbs(
                breadcrumbSetter(
                    trialIdNumber,
                    groupIdNumber,
                    trialGroupIdNumber
                )
            );
            setDrawerItems([]);
            createTrialGroupLaboratory(null, trialGroupIdNumber);
        } else if (trialGroupLaboratory && !formInitalised) {
            loadReduxForm();
            setFormInitalised(true);
        }

        if (trialGroupIdNumber && !trialGroup) {
            loadTrialGroup(trialGroupIdNumber);
        }
    }, [
        trialIdNumber,
        groupIdNumber,
        trialGroupIdNumber,
        trialGroupLaboratoryIdNumber,
        trialGroupLaboratory,
        permissions,
        formInitalised,
        setDrawerItems,
        setBreadcrumbs,
        loadTrialGroupLaboratory,
        trialGroup,
        loadTrialGroup,
        TrialGroupLaboratoryDetailsBreadcrumbs,
        TrialGroupLaboratoryCreateBreadcrumbs,
        setFormInitalised,
    ]);

    // Navigate to error page on load failure
    useEffect(() => {
        if (loadTrialGroupLaboratoryFailure) {
            navigate(`/error/${loadTrialGroupLaboratoryErrorCode}`);
        }
        if (loadTrialGroupFailure) {
            navigate(`/error/${loadTrialGroupErrorCode}`);
        }
    }, [
        loadTrialGroupLaboratoryFailure,
        loadTrialGroupFailure,
        loadTrialGroupLaboratoryErrorCode,
        loadTrialGroupErrorCode,
        navigate,
    ]);

    // Update breadcrumbs
    useEffect(() => {
        const baseRoute = location?.pathname?.split("/")[1];

        if (
            trialIdNumber &&
            trialGroupIdNumber &&
            trialGroupLaboratoryIdNumber &&
            trialGroupLaboratory?.trialGroup &&
            trialGroupLaboratory?.laboratory
        ) {
            const breadcrumbSetter =
                baseRoute === "trials"
                    ? TrialGroupLaboratoryDetailsBreadcrumbs
                    : GroupTrialLaboratoryDetailsBreadcrumbs;

            const drawerItemSetter =
                baseRoute === "trials"
                    ? TrialGroupLabSubMenu
                    : GroupTrialLabSubMenu;

            setDrawerItems(drawerItemSetter(trialGroupLaboratory, permissions));
            setBreadcrumbs(
                breadcrumbSetter(
                    trialIdNumber,
                    groupIdNumber,
                    trialGroupIdNumber,
                    trialGroupLaboratoryIdNumber,
                    trialGroupLaboratory.trialGroup?.trial?.displayName,
                    trialGroupLaboratory.trialGroup?.group?.name,
                    trialGroupLaboratory.laboratory?.name
                )
            );
        } else if (
            trialIdNumber &&
            groupIdNumber &&
            trialGroupIdNumber &&
            !trialGroupLaboratoryIdNumber &&
            trialGroup
        ) {
            const breadcrumbSetter =
                baseRoute === "trials"
                    ? TrialGroupLaboratoryCreateBreadcrumbs
                    : GroupTrialLaboratoryCreateBreadcrumbs;

            setBreadcrumbs(
                breadcrumbSetter(
                    trialIdNumber,
                    groupIdNumber,
                    trialGroupIdNumber,
                    trialGroup?.trial?.displayName,
                    trialGroup?.group?.name
                )
            );
        }
    }, [
        trialIdNumber,
        trialGroupIdNumber,
        trialGroupLaboratoryIdNumber,
        trialGroupLaboratory,
        trialGroup,
        setDrawerItems,
        setBreadcrumbs,
        TrialGroupLaboratoryDetailsBreadcrumbs,
        TrialGroupLaboratoryCreateBreadcrumbs,
    ]);

    // Reload trial group laboratory after saving
    useEffect(() => {
        if (!savingTrialGroupLaboratory && saveTrialGroupLaboratorySuccess) {
            Alert.success(
                <NotificationAlert
                    alertContent={<span>Trial Group Laboratory saved successfully</span>}
                    icon={FontAwesomeIcons.Solid.CHECK}
                />
            );

            const baseRoute = location?.pathname?.split("/")[1];

            if (saveAndReturn) {
                const url =
                    baseRoute === "trials"
                        ? `/trials/${trialIdNumber}/groups/${groupIdNumber}/trialgroup/${trialGroupIdNumber}/labs`
                        : `/groups/${groupIdNumber}/trials/${trialIdNumber}/trialgroup/${trialGroupIdNumber}/labs`;
                navigate(url);
            } else if (!trialGroupLaboratoryIdNumber) {
                const url =
                    baseRoute === "trials"
                        ? `/trials/${trialIdNumber}/groups/${groupIdNumber}/trialgroup/${trialGroupIdNumber}/labs/${trialGroupLaboratory?.id}/details`
                        : `/groups/${groupIdNumber}/trials/${trialIdNumber}/trialgroup/${trialGroupIdNumber}/labs/${trialGroupLaboratory?.id}/details`;
                navigate(url);
                clearTrialGroupLaboratory();
            } else {
                loadTrialGroupLaboratory(trialGroupLaboratoryIdNumber);
            }
        }
    }, [
        savingTrialGroupLaboratory,
        saveTrialGroupLaboratorySuccess,
        loadTrialGroupLaboratory,
        trialGroupLaboratoryIdNumber,
        trialIdNumber,
        groupIdNumber,
        trialGroupIdNumber,
        navigate,
        saveAndReturn,
        clearTrialGroupLaboratory,
    ]);

    // Failed saving
    useEffect(() => {
        if (!savingTrialGroupLaboratory && saveTrialGroupLaboratoryFailure) {
            Alert.error(
                <NotificationAlert
                    alertContent={renderRegistrationFormSaveBlock()}
                    icon={FontAwesomeIcons.Solid.BAN}
                />
            );
        }
    }, [
        savingTrialGroupLaboratory,
        saveTrialGroupLaboratorySuccess,
        loadTrialGroupLaboratory,
        trialGroupLaboratoryIdNumber,
    ]);

    // Cleanup effect to clear the trial group laboratory on component unmount
    useEffect(() => {
        return () => {
            clearTrialGroupLaboratory();
            clearTrialGroup();
            resetForm(reduxFormName);
        };
    }, [clearTrialGroupLaboratory, clearTrialGroup, resetForm, reduxFormName]);

    const handleOnSubmit = async (data: any) => {
        if (!hasBeenSubmitted) {
            setHasBeenSubmitted(true);
        }

        if (data) {
            let changes: Dtos.TrialGroupLaboratory =
                {} as Dtos.TrialGroupLaboratory;

            if (!data.trialGroupId) {
                changes.trialGroupId = trialGroupIdNumber;
            }

            await saveTrialGroupLaboratory(Object.assign({ ...data }, changes));

            if (data.saveAndReturn) {
                setSaveAndReturn(true);
            }
        }
    };

    const handleOnSubmitFailed = (_data: Dtos.TrialGroupLaboratory) => {
        Alert.error(
            <NotificationAlert
                alertContent={renderRegistrationFormSaveBlock()}
                icon={FontAwesomeIcons.Solid.BAN}
            />
        );
    };

    const onCancel = () => {
        const baseRoute = location?.pathname?.split("/")[1];
        const url =
            baseRoute === "trials"
                ? `/trials/${trialIdNumber}/groups/${groupIdNumber}/trialgroup/${trialGroupIdNumber}/labs`
                : `/groups/${groupIdNumber}/trials/${trialIdNumber}/trialgroup/${trialGroupIdNumber}/labs`;
        navigate(url);
    };

    const loadReduxForm = () => {
        let extraData: Dtos.TrialGroupLaboratory =
            {} as Dtos.TrialGroupLaboratory;

        loadForm(
            reduxFormName,
            Object.assign({ ...trialGroupLaboratory }, extraData)
        );
    };

    // Debounce validation after the initial submit to clear form validation errors as the form get updated.
    useEffect(() => {
        const debounceValidate = debounce(async () => {
            let changes: Dtos.TrialGroupLaboratory =
                {} as Dtos.TrialGroupLaboratory;

            if (form && hasBeenSubmitted && !validatingTrialGroupLaboratory) {
                await validateTrialGroupLaboratory(
                    Object.assign({ ...form }, changes)
                );
            }
        }, 250);

        debounceValidate();

        return () => {
            debounceValidate.cancel();
        };
    }, [form]);

    const renderContent = useMemo(() => {
        const hasPermission: boolean =
            AuthenticationHelper.hasCreateEditPermissionFor(
                undefined,
                trialGroupLaboratoryIdNumber,
                permissions,
                Dtos.Permission.TrialGroupLabUpdate,
                Dtos.Permission.TrialGroupLabCreate
            );
        const formDisabled: boolean =
            !hasPermission ||
            savingTrialGroupLaboratory ||
            (formState && formState.formStatus == Dtos.FormStatus.Disabled);

        let formComponents: { [index: string]: RegistrationFormComponent } = {
            detailsGroup: {
                type: RegistrationComponent.RegistrationElement,
                component: (
                    <>
                        <div className="card card-shadow" style={{ zIndex: 1 }}>
                            <div className="form-row py-4 px-4 align-items-center">
                                <div className="col-md-6">
                                    {renderFormControls(
                                        form,
                                        "trialGroupLaboratory",
                                        {
                                            laboratoryId: {
                                                type: RegistrationComponent.RegistrationControl,
                                                inputType: SelectFormInput,
                                                inputDisabled:
                                                    formDisabled ||
                                                    loadingTrialGroup,
                                                inputProps: {
                                                    loading: loadingTrialGroup,
                                                    options:
                                                        generateOptionsFromLookup(
                                                            findLookup(
                                                                "trialGroupLaboratory",
                                                                "laboratoryId",
                                                                lookups
                                                            ),
                                                            form.laboratoryId
                                                        ),
                                                },
                                                groupClassName:
                                                    "form-row bottom-0 border-top-0 pb-0 pt-0",
                                                labelClassName:
                                                    "col-form-label col-sm-2",
                                                fieldClassName:
                                                    "col-sm-9 generic-form-field",
                                            },
                                        },
                                        lookups,
                                        validationFailures,
                                        formProperties,
                                        changeForm
                                    )}
                                </div>
                                <div className="col-1 d-flex justify-content-end">
                                    {renderFormControls(
                                        form,
                                        "trialGroupLaboratory",
                                        {
                                            active: {
                                                type: RegistrationComponent.RegistrationControl,
                                                inputType: CheckboxFormInput,
                                                inputDisabled: formDisabled,
                                                inputProps: {
                                                    label: "Active, ",
                                                },
                                                inputLabel: " ",
                                                groupClassName:
                                                    "form-row bottom-0 border-top-0 pb-0 pt-0",
                                                labelClassName:
                                                    "col-form-label col-sm-5 d-none",
                                                fieldClassName:
                                                    "col-sm-12 generic-form-field d-flex",
                                                changeAction: (
                                                    _model,
                                                    value,
                                                    _form,
                                                    _parentModel
                                                ) => {
                                                    if (value) {
                                                        changeForm(
                                                            reduxFormName +
                                                                ".dateCeased",
                                                            null
                                                        );
                                                    }
                                                    changeForm(
                                                        reduxFormName +
                                                            ".active",
                                                        value
                                                    );
                                                },
                                            },
                                        },
                                        lookups,
                                        validationFailures,
                                        formProperties,
                                        changeForm
                                    )}
                                </div>
                                <div className="col-md-5">
                                    {renderFormControls(
                                        form,
                                        "trialGroupLaboratory",
                                        {
                                            dateCeased: {
                                                type: RegistrationComponent.RegistrationControl,
                                                inputType:
                                                    DatePickerFormInputFabric,
                                                inputDisabled:
                                                    formDisabled || form.active,
                                                groupClassName:
                                                    "form-row bottom-0 border-top-0 pb-0 pt-0",
                                                labelClassName:
                                                    "col-form-label col-sm-2",
                                                fieldClassName:
                                                    "col-sm-10 generic-form-field",
                                            },
                                        },
                                        lookups,
                                        validationFailures,
                                        formProperties,
                                        changeForm
                                    )}
                                </div>
                            </div>
                        </div>
                    </>
                ),
            },
        };

        return (
            <>
                <div className="row">
                    <div className={"col-auto mb-2"}>
                        <h1>Details</h1>
                    </div>
                    <div
                        className="col d-flex justify-content-end align-items-stretch"
                        style={{ height: "38px" }}
                    >
                        {AuthenticationHelper.hasPermission(
                            permissions,
                            Dtos.Permission.LabView
                        ) && trialGroupLaboratoryIdNumber ? (
                            <Link
                                className={"btn btn-primary"}
                                url={`/labs/${trialGroupLaboratory?.laboratoryId}/details`}
                                target="_blank"
                            >
                                {`Primary  `}
                                <FontAwesomeIcon
                                    icon={
                                        FontAwesomeIcons.Duotone
                                            .ARROWUPRIGHTFROMSQUARE
                                    }
                                />
                            </Link>
                        ) : null}
                    </div>
                </div>
                <Form
                    model={reduxFormName}
                    onSubmit={(val, _event) => handleOnSubmit(val)}
                    onSubmitFailed={(val) => handleOnSubmitFailed(val)}
                >
                    {renderFormControls(
                        form,
                        "trialGroupLaboratory",
                        formComponents,
                        lookups,
                        validationFailures,
                        formProperties,
                        changeForm
                    )}
                    <div className="mt-4 mb-4 text-right">
                        <button
                            disabled={formDisabled}
                            onClick={() => {
                                changeForm(
                                    reduxFormName + ".saveAndReturn",
                                    false
                                );
                            }}
                            type="submit"
                            className="btn btn-primary mr-2"
                        >
                            Submit
                        </button>
                        <button
                            disabled={formDisabled}
                            onClick={() => {
                                changeForm(
                                    reduxFormName + ".saveAndReturn",
                                    true
                                );
                            }}
                            type="submit"
                            className="btn btn-primary mr-2"
                        >
                            Submit And Return
                        </button>
                        <button
                            onClick={onCancel}
                            className="btn btn-primary mr-2"
                        >
                            Cancel
                        </button>
                    </div>
                </Form>
            </>
        );
    }, [
        AuthenticationHelper,
        trialGroupLaboratoryIdNumber,
        permissions,
        savingTrialGroupLaboratory,
        formState,
        handleOnSubmit,
        handleOnSubmitFailed,
        renderFormControls,
        lookups,
        validationFailures,
        formProperties,
        form,
        changeForm,
        reduxFormName,
        onCancel,
        trialGroup,
        trialGroupLaboratory,
    ]);

    return (
        <RestrictedLayout
            permission={Dtos.Permission.TrialGroupLabView}
            loading={
                loadingTrialGroupLaboratory ||
                creatingTrialGroupLaboratory ||
                savingTrialGroupLaboratory
            }
        >
            {renderContent}
        </RestrictedLayout>
    );
};

const mapStateToProps = (
    state: ITmdState
): Partial<TrialGroupLaboratoryPageProps> => {
    let trialGroupLaboratory: Dtos.TrialGroupLaboratory | undefined = !(
        state.trialGroupLaboratory.formData instanceof Array
    )
        ? state.trialGroupLaboratory.formData
        : undefined;
    let trialGroup: Dtos.TrialGroup | undefined = !(
        state.trialGroup.formData instanceof Array
    )
        ? state.trialGroup.formData
        : undefined;
    let permissions = state.user.userSummary
        ? state.user.userSummary.permissions
        : [];

    return {
        trialGroupLaboratory: trialGroupLaboratory!,
        loadingTrialGroupLaboratory:
            state.trialGroupLaboratory.loadState &&
            state.trialGroupLaboratory.loadState.status == RequestState.Pending,
        loadTrialGroupLaboratorySuccess:
            state.trialGroupLaboratory.loadState &&
            state.trialGroupLaboratory.loadState.status == RequestState.Success,
        loadTrialGroupLaboratoryFailure:
            state.trialGroupLaboratory.loadState &&
            state.trialGroupLaboratory.loadState.status == RequestState.Failure,
        loadTrialGroupLaboratoryErrorCode:
            state.trialGroupLaboratory.loadState.errorCode,
        savingTrialGroupLaboratory:
            state.trialGroupLaboratory.saveState &&
            state.trialGroupLaboratory.saveState.status == RequestState.Pending,
        saveTrialGroupLaboratorySuccess:
            state.trialGroupLaboratory.saveState &&
            state.trialGroupLaboratory.saveState.status == RequestState.Success,
        saveTrialGroupLaboratoryFailure:
            state.trialGroupLaboratory.saveState &&
            state.trialGroupLaboratory.saveState.status == RequestState.Failure,
        creatingTrialGroupLaboratory:
            state.trialGroupLaboratory.createState &&
            state.trialGroupLaboratory.createState.status ==
                RequestState.Pending,
        createTrialGroupLaboratorySuccess:
            state.trialGroupLaboratory.createState &&
            state.trialGroupLaboratory.createState.status ==
                RequestState.Success,
        createTrialGroupLaboratoryFailure:
            state.trialGroupLaboratory.createState &&
            state.trialGroupLaboratory.createState.status ==
                RequestState.Failure,
        validatingTrialGroupLaboratory:
            state.trialGroupLaboratory.validateState &&
            state.trialGroupLaboratory.validateState.status ==
                RequestState.Pending,
        user: state.user.data,
        permissions: permissions,
        lookups: state.trialGroupLaboratory.lookups,
        form: state.reduxForms.trialGroupLaboratory,
        formState: state.trialGroupLaboratory.formState,
        formProperties: state.trialGroupLaboratory.formProperties,
        reduxFormState: state.reduxForms.formState.trialGroupLaboratory,
        validationFailures: trialGroupLaboratory
            ? state.trialGroupLaboratory.validationFailures
            : undefined,
        trialGroup: trialGroup!,
        loadingTrialGroup:
            state.trialGroup.loadState &&
            state.trialGroup.loadState.status == RequestState.Pending,
        loadTrialGroupSuccess:
            state.trialGroup.loadState &&
            state.trialGroup.loadState.status == RequestState.Success,
        loadTrialGroupFailure:
            state.trialGroup.loadState &&
            state.trialGroup.loadState.status == RequestState.Failure,
        loadTrialGroupErrorCode: state.trialGroup.loadState.errorCode,
    };
};

const mapDispatchToProps = (
    dispatch
): Partial<TrialGroupLaboratoryPageProps> => {
    return {
        loadForm: bindActionCreators(actions.load, dispatch),
        changeForm: bindActionCreators(actions.change, dispatch),
        resetForm: bindActionCreators(actions.reset, dispatch),
        loadTrialGroupLaboratory: bindActionCreators(
            TrialGroupLaboratoryActions.LoadTrialGroupLaboratoryById,
            dispatch
        ),
        clearTrialGroupLaboratory: bindActionCreators(
            TrialGroupLaboratoryActions.Clear,
            dispatch
        ),
        saveTrialGroupLaboratory: bindActionCreators(
            TrialGroupLaboratoryActions.SaveTrialGroupLaboratory,
            dispatch
        ),
        createTrialGroupLaboratory: bindActionCreators(
            TrialGroupLaboratoryActions.CreateTrialGroupLaboratory,
            dispatch
        ),
        validateTrialGroupLaboratory: bindActionCreators(
            TrialGroupLaboratoryActions.ValidateTrialGroupLaboratory,
            dispatch
        ),
        setBreadcrumbs: bindActionCreators(
            NavigationActions.SetBreadcrumbs,
            dispatch
        ),
        setDrawerItems: bindActionCreators(
            NavigationActions.SetDrawerItems,
            dispatch
        ),
        loadTrialGroup: bindActionCreators(
            TrialGroupActions.LoadTrialGroupById,
            dispatch
        ),
        clearTrialGroup: bindActionCreators(TrialGroupActions.Clear, dispatch)
    };
};

export default connect(
    mapStateToProps,
    mapDispatchToProps
)(TrialGroupLaboratoryPage);
